import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  PACKAGE_DELIVERABLES,
  type DeliverableItem,
  type BasePackageTier,
  getBasePackageDeliverables,
  groupDeliverablesByCategory
} from "@/lib/package-config";
import { CATEGORY_CONFIG } from "@/lib/category-config";
import { Check, Info, Smartphone, Speaker, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface CustomPackageBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedDeliverables: string[], basePackageTier: BasePackageTier) => void;
  initialDeliverables?: string[];
  initialBasePackageTier?: BasePackageTier;
}

export function CustomPackageBuilderModal({
  isOpen,
  onClose,
  onSave,
  initialDeliverables = [],
  initialBasePackageTier = 'set-core',
}: CustomPackageBuilderModalProps) {
  const [selectedDeliverables, setSelectedDeliverables] = useState<string[]>(initialDeliverables);
  const [basePackageTier, setBasePackageTier] = useState<BasePackageTier>(initialBasePackageTier);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedDeliverables(initialDeliverables);
      setBasePackageTier(initialBasePackageTier);
      setOpenPopoverId(null); // Close any open popovers when modal opens
    }
  }, [isOpen, initialDeliverables, initialBasePackageTier]);

  // Get base package deliverables based on selected tier
  const basePackageDeliverables = getBasePackageDeliverables(basePackageTier);

  // Get names of items included in base package
  const basePackageItemNames = new Set(basePackageDeliverables.map(item => item.name));

  // Get optional add-ons (items NOT in the selected base package)
  const optionalDeliverables = PACKAGE_DELIVERABLES.filter(item => !basePackageItemNames.has(item.name));

  // Group optional deliverables by category
  const groupedDeliverables = groupDeliverablesByCategory(optionalDeliverables);

  const handleToggleDeliverable = (name: string) => {
    setSelectedDeliverables(prev =>
      prev.includes(name)
        ? prev.filter(d => d !== name)
        : [...prev, name]
    );
  };

  const handleBasePackageTierChange = (newTier: BasePackageTier) => {
    setBasePackageTier(newTier);

    // Auto-remove any selected add-ons that are now included in the new base package
    const newBasePackageDeliverables = getBasePackageDeliverables(newTier);
    const newBasePackageItemNames = new Set(newBasePackageDeliverables.map(item => item.name));
    setSelectedDeliverables(prev => prev.filter(name => !newBasePackageItemNames.has(name)));
  };

  const handleSave = () => {
    onSave(selectedDeliverables, basePackageTier);
    onClose();
  };

  const renderDeliverableItem = (item: DeliverableItem, idx: number) => {
    const itemId = `${item.name}-${idx}`;
    const isPopoverOpen = openPopoverId === itemId;
    const hasDetails = item.details !== undefined;

    return (
      <div
        key={idx}
        className={cn(
          "flex items-center gap-2 p-2 rounded border transition-all",
          selectedDeliverables.includes(item.name)
            ? "border-green-500 bg-green-50"
            : "border-gray-200 hover:border-green-300"
        )}
      >
        <Checkbox
          checked={selectedDeliverables.includes(item.name)}
          onCheckedChange={() => handleToggleDeliverable(item.name)}
          className="flex-shrink-0"
        />

        <Popover open={isPopoverOpen} onOpenChange={(open) => setOpenPopoverId(open ? itemId : null)}>
          <PopoverTrigger asChild>
            <div className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer group">
              <span
                className={cn(
                  "flex-1 text-sm font-medium leading-tight transition-colors",
                  hasDetails && "hover:text-green-600"
                )}
              >
                {item.name}
                {(item.setAudio.note || item.setVideo.note) && (
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {item.setAudio.note || item.setVideo.note}
                  </span>
                )}
              </span>
              {hasDetails && (
                <Info className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
              )}
            </div>
          </PopoverTrigger>
          {hasDetails && item.details && (
            <PopoverContent side="left" align="start" className="w-80">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-green-600">{item.details.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.details.description}
                </p>
                {item.details.imageUrl && (
                  <div className="rounded-lg overflow-hidden border bg-gray-50">
                    <img
                      src={item.details.imageUrl}
                      alt={item.details.title}
                      className="w-full h-auto"
                      onError={(e) => {
                        (e.target as HTMLElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </PopoverContent>
          )}
        </Popover>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-600">
            Create Custom Package
          </DialogTitle>
          <DialogDescription>
            Select your base package and add optional upgrades to create your perfect package
          </DialogDescription>
        </DialogHeader>

        {/* Base Package Tier Selector */}
        <div className="border-b pb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Select Base Package</h3>
          <div className="grid grid-cols-3 gap-3">
            {/* SET Core Option */}
            <button
              onClick={() => handleBasePackageTierChange('set-core')}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                basePackageTier === 'set-core'
                  ? "border-gray-600 bg-gray-50 shadow-md"
                  : "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
              )}
            >
              <Smartphone className={cn("w-6 h-6", basePackageTier === 'set-core' ? "text-gray-600" : "text-gray-400")} />
              <div className="text-center">
                <div className={cn("font-semibold text-sm", basePackageTier === 'set-core' ? "text-gray-900" : "text-gray-600")}>
                  SET Core
                </div>
                <div className="text-xs text-gray-500 mt-0.5">Essential features</div>
              </div>
            </button>

            {/* SET Audio Option */}
            <button
              onClick={() => handleBasePackageTierChange('set-audio')}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                basePackageTier === 'set-audio'
                  ? "border-blue-600 bg-blue-50 shadow-md"
                  : "border-gray-200 hover:border-blue-400 hover:bg-blue-50"
              )}
            >
              <Speaker className={cn("w-6 h-6", basePackageTier === 'set-audio' ? "text-blue-600" : "text-gray-400")} />
              <div className="text-center">
                <div className={cn("font-semibold text-sm", basePackageTier === 'set-audio' ? "text-blue-900" : "text-gray-600")}>
                  SET Audio
                </div>
                <div className="text-xs text-gray-500 mt-0.5">Core + Audio</div>
              </div>
            </button>

            {/* SET Video Option */}
            <button
              onClick={() => handleBasePackageTierChange('set-video')}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
                basePackageTier === 'set-video'
                  ? "border-purple-600 bg-purple-50 shadow-md"
                  : "border-gray-200 hover:border-purple-400 hover:bg-purple-50"
              )}
            >
              <Video className={cn("w-6 h-6", basePackageTier === 'set-video' ? "text-purple-600" : "text-gray-400")} />
              <div className="text-center">
                <div className={cn("font-semibold text-sm", basePackageTier === 'set-video' ? "text-purple-900" : "text-gray-600")}>
                  SET Video
                </div>
                <div className="text-xs text-gray-500 mt-0.5">Full package</div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex-1 flex gap-6 overflow-hidden">
          {/* Left Panel: Base Package Deliverables (dynamic based on selection) */}
          <div className="w-1/3 border-r pr-6 flex flex-col">
            <div className="pb-3">
              <h3 className="font-semibold text-lg mb-1 text-gray-700">
                {basePackageTier === 'set-core' && 'SET Core Included'}
                {basePackageTier === 'set-audio' && 'SET Audio Included'}
                {basePackageTier === 'set-video' && 'SET Video Included'}
              </h3>
              <p className="text-xs text-gray-600">
                Base package deliverables
              </p>
            </div>

            <div className="space-y-2 mt-3">
              {basePackageDeliverables.map((item, idx) => {
                // Get the appropriate note based on selected tier
                const note = basePackageTier === 'set-core' ? item.setCore.note :
                            basePackageTier === 'set-audio' ? item.setAudio.note :
                            item.setVideo.note;

                return (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-700 leading-tight">{item.name}</div>
                      {note && (
                        <div className="text-xs text-gray-500 mt-0.5">{note}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Panel: Optional Add-ons - SCROLLABLE with STICKY HEADER */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="pb-3 flex-shrink-0">
              <h3 className="font-semibold text-lg mb-1 text-gray-900">
                Add Optional Upgrades
              </h3>
              <p className="text-xs text-gray-600">
                Select additional features to customize your package
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">
                {/* Column 1: Mobile Experience + In Show/Venue */}
                <div className="space-y-4">
                  {/* Mobile Experience Section */}
                  {groupedDeliverables.mobile.length > 0 && (
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 py-1.5 px-2 mb-1.5 rounded border-l-4",
                        CATEGORY_CONFIG.mobile.bgColor,
                        CATEGORY_CONFIG.mobile.borderColor
                      )}>
                        <CATEGORY_CONFIG.mobile.icon className={cn("w-4 h-4", CATEGORY_CONFIG.mobile.textColor)} />
                        <h4 className={cn("text-sm font-semibold", CATEGORY_CONFIG.mobile.textColor)}>
                          {CATEGORY_CONFIG.mobile.title}
                        </h4>
                      </div>
                      <div className="space-y-1.5">
                        {groupedDeliverables.mobile.map((item, idx) => renderDeliverableItem(item, idx))}
                      </div>
                    </div>
                  )}

                  {/* In Show/Venue Section */}
                  {groupedDeliverables.venue.length > 0 && (
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 py-1.5 px-2 mb-1.5 rounded border-l-4",
                        CATEGORY_CONFIG.venue.bgColor,
                        CATEGORY_CONFIG.venue.borderColor
                      )}>
                        <CATEGORY_CONFIG.venue.icon className={cn("w-4 h-4", CATEGORY_CONFIG.venue.textColor)} />
                        <h4 className={cn("text-sm font-semibold", CATEGORY_CONFIG.venue.textColor)}>
                          {CATEGORY_CONFIG.venue.title}
                        </h4>
                      </div>
                      <div className="space-y-1.5">
                        {groupedDeliverables.venue.map((item, idx) => renderDeliverableItem(item, idx))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Column 2: Social/Media */}
                <div className="space-y-4">
                  {/* Social/Media Section */}
                  {groupedDeliverables.media.length > 0 && (
                    <div>
                      <div className={cn(
                        "flex items-center gap-2 py-1.5 px-2 mb-1.5 rounded border-l-4",
                        CATEGORY_CONFIG.media.bgColor,
                        CATEGORY_CONFIG.media.borderColor
                      )}>
                        <CATEGORY_CONFIG.media.icon className={cn("w-4 h-4", CATEGORY_CONFIG.media.textColor)} />
                        <h4 className={cn("text-sm font-semibold", CATEGORY_CONFIG.media.textColor)}>
                          {CATEGORY_CONFIG.media.title}
                        </h4>
                      </div>
                      <div className="space-y-1.5">
                        {groupedDeliverables.media.map((item, idx) => renderDeliverableItem(item, idx))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm text-gray-600">
              {selectedDeliverables.length} optional upgrade{selectedDeliverables.length !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={selectedDeliverables.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {initialDeliverables.length > 0 ? 'Update Package' : 'Create Custom Package'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
